/*eslint @typescript-eslint/no-empty-function: "off"*/
import React from 'react';
import { DeleteState } from 'store/entity/types';
import { display } from 'components/ErrorView/ErrorDialog';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { black, lightGreenSolid } from 'utils/style/colors';
import { LoadingLine } from './LoadingLine';
import { IThisUser } from 'store/store';
import { IAssignmentView, ITask, ITasks } from 'store/tasks/tasksType';
import { smartDateDuration, smartDateString } from '../../../utils/date';
import moment from 'moment-jalaali';
import messages from './Messages';

const getMarkerDay = (d: Date) => {
  const day = new Intl.DateTimeFormat('en-US-u-ca-persian', {
    day: 'numeric',
    // timeZone: 'Asia/Tehran',
    timeZoneName: 'short',
  }).format(d);

  const ret = day.split(',')[0];
  //
  return Number(ret);
};

const getPersianOffset = () => {
  const d = new Date();
  d.setDate(15);
  const pd = getMarkerDay(d);
  const offset = 15 - pd;
  return offset;
};
export const getStartDayOfWeek = getPersianOffset() + 13;

interface IComponentProps {
  tasks: ITask[];
  loading: boolean;
  thisUser: IThisUser;
  companyId: number;
  projectId?: number;
  onUpdateTask: (taskView: ITask) => void;
  requireTasks: (
    deleteState?: DeleteState,
    startDate?: Date,
    endDate?: Date
  ) => Promise<any>;
  showProgress: boolean;
  showWeight: boolean;
  taskSelected?: number | boolean;
  companyOwnerId: number;
  allTasksFetched: boolean;
  unfilteredTasks: ITasks['data'];
  templateSelected: boolean;
  handleClickOnTask: (taskId: number) => void;
  onUpdateAssignment: (assignment: IAssignmentView) => void;
  setDateRange: (start: Date, end: Date) => void;
  initialDates?: { start: Date; end: Date };
}

interface IComponentState {
  loading: boolean;
  requestSent: boolean;
  loadMoreButton: boolean;
  customTitle: string;
}

const getMonthName = (a: Date) => {
  return smartDateString(a, false, false, false);
};

class CalendarView extends React.Component<IComponentProps> {
  state: IComponentState = {
    loading: this.props.loading,
    requestSent: false,
    loadMoreButton: false,
    customTitle:
      smartDateString(new Date(new Date().setDate(15)), false, false, false) +
      'init',
  };
  calendarRef = React.createRef<any>();

  componentDidMount() {
    if (this.calendarRef.current) {
      const calendarApi = this.calendarRef.current.getApi();
      calendarApi.on('eventClick', (a: any) => {
        this.props.handleClickOnTask(Number(a.event.id));
      });
    }
  }

  componentWillUnmount() {}

  loadData = (start: Date, end: Date) => {
    this.setState({ requestSent: true, loading: true });
    this.props.requireTasks(undefined, start, end).then(
      () => {
        this.setState({
          loading: false,
          requestSent: false,
          loadMoreButton: false,
        });
      },
      (err: any) => {
        display(err);
        this.setState({ loadMoreButton: true, loading: false });
      }
    );
  };

  render() {
    const { tasks, loading, taskSelected } = this.props;
    const events = Object.values(tasks).reduce((list, task) => {
      const taskItem = {
        id: task.id,
        title: task.textTitle,
        allDay: true,
      };
      if (taskItem.id === taskSelected) {
        taskItem['backgroundColor'] = lightGreenSolid;
        taskItem['textColor'] = black;
      }
      if (task.startDate && task.dueDate) {
        taskItem['start'] = task.startDate;
        taskItem['end'] = task.dueDate;
      } else if (task.startDate || task.dueDate) {
        taskItem['date'] = task.dueDate || task.startDate;
      } else {
        return list!;
      }
      taskItem.allDay = !task.dateAndTime;

      list?.push(taskItem);
      return list!;
    }, [] as any[]);
    return (
      <>
        {
          <div
            className="row"
            style={{ width: '100%', padding: 0, height: '5px' }}
          >
            {loading && <LoadingLine />}
          </div>
        }
        <FullCalendar
          plugins={[dayGridPlugin, multiMonthPlugin]}
          views={{
            multiMonthYear: {
              type: 'multiMonth',
              showNonCurrentDates: true,
              multiMonthTitleFormat: (e: any) => {
                return smartDateString(
                  new Date(e.date.marker).setDate(28),
                  false,
                  true,
                  false
                );
              },
            },
          }}
          // timeZone="Asia/Tehran"
          locale="fa"
          customButtons={{
            customTitle: {
              text: this.state.customTitle,
            },
          }}
          headerToolbar={{
            start: 'customTitle',
            center: 'dayGridMonth,multiMonthYear',
            end: 'next,today,prev',
          }}
          titleRangeSeparator={' تا '}
          titleFormat={{
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            formatMatcher: 'best fit',
          }}
          buttonText={{
            today: messages.today,
            month: messages.month,
            week: messages.week,
            list: messages.list,
            year: messages.year,
          }}
          buttonIcons={{
            prev: 'chevron-right',
            next: 'chevron-left',
          }}
          initialDate={new Date()}
          datesSet={(a: any) => {
            const length =
              (a.end.getTime() - a.start.getTime()) / (24 * 60 * 60 * 1000);
            if (length > 25 && length < 50) {
              this.setState({
                customTitle: getMonthName(
                  new Date((a.end.getTime() + a.start.getTime()) / 2)
                ),
              });
            } else
              this.setState({
                customTitle: smartDateDuration(a.start, a.end),
              });
            this.props.setDateRange(
              new Date(a.start.getTime() - 30 * 60 * 60 * 24 * 1000),
              new Date(a.end.getTime() + 30 * 60 * 60 * 24 * 1000)
            );
          }}
          dayCellClassNames={(e: any) => {
            if (e.view.type.startsWith('dayGrid')) {
              if (getMonthName(e.date) !== this.state.customTitle)
                return 'inactiveCalendarDate';
            } else if (e.view.type.startsWith('multiMonth')) {
              const jDate = moment(e.date).jDate();
              if (jDate < 13 && e.isOther) return 'inactiveCalendarDate';
              if (jDate > 21 && !e.isOther) return 'inactiveCalendarDate';
            }

            return 'activeCalendarDate';
          }}
          nowIndicator={true}
          dayMaxEvents={3}
          dayMaxEventRows={3}
          height={'100%'}
          ref={this.calendarRef}
          events={events}
          firstDay={getStartDayOfWeek}
          slotMinTime={'6:00:00'}
          slotMaxTime={'22:00:00'}
          allDayText={messages.allDay}
          businessHours={{
            daysOfWeek: [6, 0, 1, 2, 3, 4],
            startTime: '7:00',
            endTime: '17:00',
          }}
          direction={'rtl'}
        />
      </>
    );
  }
}

export default CalendarView;
